import {
  Component,
  input,
  output,
  ChangeDetectionStrategy,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  AlertService,
  ButtonLoadingDirective,
  CardComponent,
  CardContentComponent,
  IconComponent,
} from '@gains-com/ui-angular';
import { CurrencyPipe, DatePipe, TitleCasePipe } from '@angular/common';
import { RedemptionAndPayment } from '../../../../core/payments-common';
import { RedemptionDialogHeaderComponent } from '../redemption-dialog-header/redemption-dialog-header.component.js';
import { LottieAnimationComponent } from '../../../lottie-animation/lottie-animation.component.js';
import { decimal } from '@gains-com/shared';
import { MatButton } from '@angular/material/button';
import { RedemptionsService } from '../../../../core/redemption.service.js';
import { LOGGER_FACTORY } from '../../../../core/logger/logger-factory.provider.js';

@Component({
  selector: 'app-coinflow-confirm-redemption',
  imports: [
    CardComponent,
    CardContentComponent,
    RedemptionDialogHeaderComponent,
    CurrencyPipe,
    TitleCasePipe,
    IconComponent,
    LottieAnimationComponent,
    DatePipe,
    MatButton,
    ButtonLoadingDirective,
  ],
  templateUrl: './coinflow-confirm-redemption.component.html',
  styleUrls: ['../styles.scss', './coinflow-confirm-redemption.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CoinflowConfirmRedemptionComponent {
  private readonly _logger = inject(LOGGER_FACTORY).create('RedemptionsDialog');
  private readonly _alert = inject(AlertService);

  redemption = input.required<RedemptionAndPayment>();
  private readonly _redemptionsService = inject(RedemptionsService);
  cancelInProgress = signal(false);

  setAmount = output<number>();
  close = output<'contact-support' | 'additional-verification' | null>();
  cancel = output<void>();

  async cancelRedemption() {
    const redemption = this.redemption();
    if (!redemption) return;

    this.cancelInProgress.set(true);

    try {
      await this._redemptionsService.cancelRedemption(redemption.redemption_id);
    } catch (error: any) {
      if (error?.code !== 'INVALID_REDEMPTION_STATUS') {
        this._logger.error('Failed to cancel redemption', {
          error: error.message,
          redemptionId: redemption.redemption_id,
        });
        this._alert.notifyDanger(
          'Cancellation Failed',
          'Unable to cancel redemption. Please try again or contact support.',
        );
      }
    } finally {
      this.cancelInProgress.set(false);
    }
  }

  totalRedemptionAmount = computed(() =>
    decimal(this.redemption().amount)
      .minus(this.redemption().transaction_fee_amount ?? 0)
      .toNumber(),
  );
  blockedSubtitle = computed(
    () =>
      'Your redemption has been submitted and is currently under review. All redemptions are reviewed by our team and are typically processed the same day they are submitted.',
  );
}
